/**
 * DmLegacyRedirect — a pre-shell `/dm-*` address forwarded into `/dm/*` (gh#2607 PR-3).
 *
 * ── THE QUERY STRING TRAVELS, AND THAT IS THE WHOLE POINT ───────────────────
 * `<Navigate to="/dm/board" />` replaces the WHOLE location, search included, and
 * a `/dm-project` link carries the query `dmProjectViewUrl` serialised panes, mode, the
 * inspected item and the `?project=` guard into. A redirect that dropped it would turn every
 * shared workspace link into a bare board — silently, because the page still renders
 * and looks fine to whoever opened it.
 *
 * ── IT FORWARDS THE STRING; IT DOES NOT RE-SERIALISE IT ─────────────────────
 * The obvious implementation — parse into `URLSearchParams`, write back with
 * `toString()` — is a SECOND writer of a wire format `dmProjectViewUrl` is declared the
 * only authority over. It also visibly rewrites the address: `?panes=3,7` comes back out
 * as `?panes=3%2C7`, which decodes to the same value and is still a different link from
 * the one the operator sent. So the search is passed through BYTE FOR BYTE.
 *
 * The redirect REPLACES: a legacy address left in history sends the back button straight
 * back through this component, which immediately forwards again — the operator cannot
 * leave the page they just left.
 */
import { Navigate, useLocation } from 'react-router-dom';

interface DmLegacyRedirectProps {
  /** The shell path to land on. */
  pathname: string;
}

export function DmLegacyRedirect({ pathname }: DmLegacyRedirectProps) {
  const { search, hash } = useLocation();
  // The HASH travels for the same reason the search does, and it is easier to lose:
  // `to` accepts a partial Path and silently defaults every member left out, so a
  // fragment omitted here is dropped with no error and no visible symptom beyond the
  // reader landing at the top of a page they were sent to a place inside.
  return <Navigate to={{ pathname, search, hash }} replace />;
}
